import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Trash2, Download, Upload, RefreshCw, LogOut, Plus, MessageCircle } from "lucide-react";
import { ImageField, MediaTab, StoryTab, FaqTab, GalleryTab } from "@/pages/AdminContent";

const tabs = ["Overview", "Guests", "Events", "Photos", "Story", "FAQs", "Gallery", "Content", "Analytics"];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("Overview");
  const logout = () => { localStorage.removeItem("nigi_admin_token"); nav("/admin/login"); };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F8F5F0]">
      <header className="border-b border-[#E8D9C5] bg-[#F8F5F0]/90 backdrop-blur sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <div className="font-serif-display text-xl text-[#292524]">NiGi · Admin</div>
          <button data-testid="admin-logout" onClick={logout} className="overline text-[#522C36] inline-flex items-center gap-2"><LogOut size={14}/>Sign out</button>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex gap-8 overflow-x-auto">
          {tabs.map((t) => (
            <button key={t} data-testid={`tab-${t.toLowerCase()}`} onClick={() => setTab(t)} className={`py-4 overline whitespace-nowrap ${tab === t ? "text-[#522C36] border-b-2 border-[#522C36]" : "text-[#292524]/60"}`}>{t}</button>
          ))}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        {tab === "Overview" && <Overview />}
        {tab === "Guests" && <Guests />}
        {tab === "Events" && <EventsTab />}
        {tab === "Photos" && <MediaTab />}
        {tab === "Story" && <StoryTab />}
        {tab === "FAQs" && <FaqTab />}
        {tab === "Gallery" && <GalleryTab />}
        {tab === "Content" && <ContentTab />}
        {tab === "Analytics" && <AnalyticsTab />}
      </main>
    </div>
  );
}

function Overview() {
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)).catch(() => setErr(true)); }, []);
  if (err) return <LoadError />;
  if (!a) return <div>Loading…</div>;
  return (
    <div data-testid="overview">
      <h1 className="font-serif-display text-4xl text-[#292524] mb-8">Welcome back.</h1>
      <div className="grid md:grid-cols-4 gap-6">
        <Stat label="Families" value={a.totals.families} />
        <Stat label="Guests" value={a.totals.people} />
        <Stat label="Confirmed" value={a.totals.confirmed} tone="good" />
        <Stat label="Pending" value={a.totals.pending} tone="warn" />
      </div>
    </div>
  );
}

function LoadError() {
  return <div data-testid="load-error" className="text-[#522C36]">Couldn't load this section. Please refresh the page.</div>;
}

function Stat({ label, value, tone }) {
  const color = tone === "good" ? "#522C36" : tone === "warn" ? "#B69A69" : "#292524";
  return (
    <div className="border border-[#E8D9C5] bg-[#F8F5F0] p-6">
      <div className="overline text-[#B69A69] mb-3">{label}</div>
      <div className="font-serif-display text-4xl" style={{ color }}>{value}</div>
    </div>
  );
}

function Guests() {
  const [list, setList] = useState([]);
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const refresh = () => api.get("/admin/families", { params: { q: q || undefined } }).then((r) => setList(r.data));
  useEffect(() => { refresh(); api.get("/admin/events").then((r) => setEvents(r.data)); }, []);
  useEffect(() => { const id = setTimeout(refresh, 200); return () => clearTimeout(id); }, [q]);

  const shareWhatsApp = (f) => {
    const url = `${window.location.origin}/invite/${f.invite_token}`;
    const text = `Dearest ${f.family_name},\n\nIt's happening!! Niyati & Girish are getting married, and it wouldn't be the same without you.\n\nHere's your personal invitation. Open it to see your celebrations and RSVP:\n${url}\n\nWith love,\nNiyati & Girish\n#MaamlaNiGiHai`;
    let phone = (f.phone || "").replace(/\D/g, "");
    if (phone.length === 10) phone = `91${phone}`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };
  const copyLink = (f) => {
    const url = `${window.location.origin}/invite/${f.invite_token}`;
    navigator.clipboard.writeText(url); toast.success("Invitation link copied");
  };
  const regen = async (f) => { if (!window.confirm(`Generate a new link for ${f.family_name}? The old link will stop working.`)) return; await api.post(`/admin/families/${f.id}/regenerate-token`); toast.success("New token generated"); refresh(); };
  const del = async (f) => { if (!confirm(`Delete ${f.family_name}?`)) return; await api.delete(`/admin/families/${f.id}`); toast.success("Deleted"); refresh(); };

  const importCsv = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/admin/families/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success(`Imported ${r.data.created} families`); refresh();
    e.target.value = "";
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("nigi_admin_token");
    const res = await fetch(`${API}/admin/families/export-csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "guests.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="guests-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h1 className="font-serif-display text-3xl text-[#292524]">Guest list</h1>
        <div className="flex gap-3 flex-wrap">
          <label className="border border-[#B69A69]/40 overline px-4 py-2 inline-flex items-center gap-2 cursor-pointer">
            <Upload size={14}/>Import CSV
            <input data-testid="csv-import" type="file" accept=".csv" onChange={importCsv} className="hidden" />
          </label>
          <button data-testid="csv-export" onClick={exportCsv} className="border border-[#B69A69]/40 overline px-4 py-2 inline-flex items-center gap-2"><Download size={14}/>Export</button>
          <button data-testid="family-new" onClick={() => setEdit({ family_name: "", side: "bride", email: "", phone: "", members: [{ name: "", is_child: false }], invited_event_slugs: [], plus_one_allowed: false, notes: "" })} className="bg-[#522C36] text-[#F8F5F0] overline px-4 py-2 inline-flex items-center gap-2"><Plus size={14}/>Add family</button>
        </div>
      </div>
      <input data-testid="guest-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search family or guest" className="w-full mb-6 bg-transparent border-b border-[#B69A69]/50 py-2 outline-none" />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left overline text-[#B69A69]">
            <th className="py-3">Family</th><th>Side</th><th>Members</th><th>Events</th><th>RSVP</th><th></th>
          </tr></thead>
          <tbody>
            {list.map((f) => {
              const rsvpStatus = f.rsvp ? (f.rsvp.responses.some((r) => r.status === "attending") ? "attending" : (f.rsvp.responses.every((r) => r.status === "declined") ? "declined" : "pending")) : "pending";
              return (
                <tr key={f.id} className="border-t border-[#E8D9C5]" data-testid={`family-row-${f.id}`}>
                  <td className="py-3 font-serif-display text-lg">{f.family_name}</td>
                  <td>{f.side}</td>
                  <td>{f.members.length}</td>
                  <td className="text-xs">{f.invited_event_slugs.join(", ")}</td>
                  <td><span className={`overline ${rsvpStatus === "attending" ? "text-[#522C36]" : rsvpStatus === "declined" ? "text-[#292524]/50" : "text-[#B69A69]"}`}>{rsvpStatus}</span></td>
                  <td>
                    <div className="flex gap-2 justify-end">
                      <button title="Send on WhatsApp" data-testid={`whatsapp-${f.id}`} onClick={() => shareWhatsApp(f)} className="p-2 hover:bg-[#E8D9C5] text-[#1F8F4E]"><MessageCircle size={14}/></button>
                      <button title="Copy link" data-testid={`copy-${f.id}`} onClick={() => copyLink(f)} className="p-2 hover:bg-[#E8D9C5]"><Copy size={14}/></button>
                      <button title="Regenerate link" data-testid={`regen-${f.id}`} onClick={() => regen(f)} className="p-2 hover:bg-[#E8D9C5]"><RefreshCw size={14}/></button>
                      <button data-testid={`edit-${f.id}`} onClick={() => setEdit({ ...f })} className="overline px-3 py-2 hover:bg-[#E8D9C5]">Edit</button>
                      <button data-testid={`delete-${f.id}`} onClick={() => del(f)} className="p-2 text-[#522C36] hover:bg-[#E8D9C5]"><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {edit && <FamilyEditor data={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); refresh(); }} events={events} />}
    </div>
  );
}

function FamilyEditor({ data, onClose, onSaved, events }) {
  const [f, setF] = useState(data);
  const isNew = !f.id;
  const memberSel = (m) => { const own = (m.event_slugs || []).filter((s) => f.invited_event_slugs.includes(s)); return own.length ? own : f.invited_event_slugs; };
  const toggleMemberEvent = (i, slug) => {
    const m = f.members[i];
    const cur = memberSel(m);
    const next = cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug];
    if (!next.length) { toast.error("Each member needs at least one celebration"); return; }
    const members = [...f.members]; members[i] = { ...m, event_slugs: next }; setF({ ...f, members });
  };
  const save = async () => {
    try {
      const memberPayload = (m) => { const sel = memberSel(m); return { id: m.id, name: m.name, is_child: !!m.is_child, dietary: m.dietary || "", allergies: m.allergies || "", event_slugs: sel.length === f.invited_event_slugs.length ? [] : sel }; };
      const payload = { family_name: f.family_name, side: f.side, email: f.email || "", phone: f.phone || "", members: f.members.map(memberPayload), invited_event_slugs: f.invited_event_slugs, plus_one_allowed: !!f.plus_one_allowed, notes: f.notes || "" };
      if (isNew) await api.post("/admin/families", payload);
      else await api.put(`/admin/families/${f.id}`, payload);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const toggleEvent = (slug) => {
    setF((p) => ({ ...p, invited_event_slugs: p.invited_event_slugs.includes(slug) ? p.invited_event_slugs.filter((s) => s !== slug) : [...p.invited_event_slugs, slug] }));
  };
  return (
    <div className="fixed inset-0 bg-[#292524]/60 flex items-center justify-center p-6 z-50" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#F8F5F0] max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8" data-testid="family-editor">
        <h2 className="font-serif-display text-2xl mb-6">{isNew ? "New family" : f.family_name}</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Family name"><input data-testid="family-name-input" value={f.family_name} onChange={(e) => setF({ ...f, family_name: e.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
          <Field label="Side"><select value={f.side} onChange={(e) => setF({ ...f, side: e.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent"><option value="bride">Bride</option><option value="groom">Groom</option><option value="friend">Friend</option></select></Field>
          <Field label="Email"><input value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
          <Field label="Phone"><input value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
        </div>

        <div className="mt-6">
          <div className="overline text-[#B69A69] mb-3">Invited events</div>
          <div className="flex flex-wrap gap-3">
            {events.map((ev) => (
              <button key={ev.slug} data-testid={`event-toggle-${ev.slug}`} onClick={() => toggleEvent(ev.slug)} className={`overline px-4 py-2 border ${f.invited_event_slugs.includes(ev.slug) ? "bg-[#522C36] text-[#F8F5F0] border-[#522C36]" : "border-[#B69A69]/40"}`}>{ev.name}</button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <div className="overline text-[#B69A69] mb-3">Family members</div>
          {f.members.map((m, i) => (
            <div key={i} className="grid md:grid-cols-5 gap-3 items-center mb-3">
              <input data-testid={`member-name-${i}`} value={m.name} onChange={(e) => { const members = [...f.members]; members[i] = { ...m, name: e.target.value }; setF({ ...f, members }); }} placeholder="Full name" className="col-span-3 border-b border-[#B69A69]/40 py-2 bg-transparent" />
              <label className="overline text-[#292524]/70 flex items-center gap-2"><input type="checkbox" checked={!!m.is_child} onChange={(e) => { const members = [...f.members]; members[i] = { ...m, is_child: e.target.checked }; setF({ ...f, members }); }} />Child</label>
              <button className="overline text-[#522C36]" onClick={() => setF({ ...f, members: f.members.filter((_, j) => j !== i) })}>Remove</button>
              {f.invited_event_slugs.length > 1 && (
                <div className="md:col-span-5 flex flex-wrap gap-2 items-center -mt-1 mb-2">
                  <span className="text-xs text-[#292524]/60 mr-1">Invited to:</span>
                  {events.filter((ev) => f.invited_event_slugs.includes(ev.slug)).map((ev) => (
                    <button key={ev.slug} type="button" data-testid={`member-${i}-event-${ev.slug}`} onClick={() => toggleMemberEvent(i, ev.slug)}
                      className={`text-[10px] uppercase tracking-wider px-2.5 py-1 border ${memberSel(m).includes(ev.slug) ? "bg-[#B69A69] text-[#F8F5F0] border-[#B69A69]" : "border-[#B69A69]/40 text-[#292524]/60"}`}>{ev.name}</button>
                  ))}
                </div>
              )}
            </div>
          ))}
          <button data-testid="add-member" onClick={() => setF({ ...f, members: [...f.members, { name: "", is_child: false }] })} className="overline text-[#522C36]">+ Add member</button>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <label className="overline text-[#292524]/70 flex items-center gap-2"><input data-testid="plus-one-toggle" type="checkbox" checked={!!f.plus_one_allowed} onChange={(e) => setF({ ...f, plus_one_allowed: e.target.checked })} />Plus-one allowed</label>
          <Field label="Notes"><input data-testid="family-notes-input" value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button onClick={onClose} className="overline px-4 py-2">Cancel</button>
          <button data-testid="family-save" onClick={save} className="bg-[#522C36] text-[#F8F5F0] overline px-6 py-3">Save</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><div className="overline text-[#B69A69] mb-1">{label}</div>{children}</div>;
}

function EventsTab() {
  const [list, setList] = useState([]);
  const refresh = () => api.get("/admin/events").then((r) => setList(r.data));
  useEffect(() => { refresh(); }, []);
  const update = async (slug, data) => {
    try { await api.put(`/admin/events/${slug}`, data); toast.success("Updated"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  return (
    <div data-testid="events-tab">
      <h1 className="font-serif-display text-3xl text-[#292524] mb-8">Events</h1>
      <div className="space-y-10">
        {list.map((e) => <EventEditor key={e.slug} e={e} onSave={update} />)}
      </div>
    </div>
  );
}

function EventEditor({ e, onSave }) {
  const [f, setF] = useState(e);
  return (
    <div className="border border-[#E8D9C5] p-6" data-testid={`event-edit-${e.slug}`}>
      <div className="font-serif-display text-2xl mb-4">{e.name}</div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Name"><input value={f.name} onChange={(x) => setF({ ...f, name: x.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
        <Field label="Date"><input type="date" value={f.date} onChange={(x) => setF({ ...f, date: x.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
        <Field label="Start time"><input data-testid={`start-time-${e.slug}`} value={f.start_time || ""} onChange={(x) => setF({ ...f, start_time: x.target.value })} placeholder="7:00 PM" className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
        <Field label="End time"><input value={f.end_time || ""} onChange={(x) => setF({ ...f, end_time: x.target.value })} placeholder="11:00 PM" className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
        <Field label="Venue"><input value={f.venue} onChange={(x) => setF({ ...f, venue: x.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
        <Field label="Dress code"><input value={f.dress_code || ""} onChange={(x) => setF({ ...f, dress_code: x.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
      </div>
      <div className="mt-4">
        <Field label="Description"><textarea value={f.description || ""} onChange={(x) => setF({ ...f, description: x.target.value })} className="w-full border border-[#B69A69]/40 p-3 bg-transparent min-h-[80px]" /></Field>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <Field label="Headline (blank = default)"><input data-testid={`headline-${e.slug}`} value={f.headline || ""} onChange={(x) => setF({ ...f, headline: x.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
        <Field label="Tagline (blank = default)"><input value={f.tagline || ""} onChange={(x) => setF({ ...f, tagline: x.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
        <Field label="Theme / vibe"><input value={f.theme || ""} onChange={(x) => setF({ ...f, theme: x.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
        <Field label="Good to know"><input value={f.instructions || ""} onChange={(x) => setF({ ...f, instructions: x.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
        <Field label="Lookbook: for her"><input value={f.styling_women || ""} onChange={(x) => setF({ ...f, styling_women: x.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
        <Field label="Lookbook: for him"><input value={f.styling_men || ""} onChange={(x) => setF({ ...f, styling_men: x.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
        <Field label="Lookbook: styling note"><input value={f.styling_note || ""} onChange={(x) => setF({ ...f, styling_note: x.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
        <Field label="Colour inspo (hex, comma separated)"><input value={(f.dress_code_colors || []).join(", ")} onChange={(x) => setF({ ...f, dress_code_colors: x.target.value.split(",").map((c) => c.trim()).filter(Boolean) })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <ImageField label="Poster / hero image" testid={`event-image-${e.slug}`} value={f.image_url} onChange={(v) => setF({ ...f, image_url: v })} />
        {[0, 1, 2].map((k) => (
          <ImageField key={k} label={`Lookbook image ${k + 1} (blank = default)`} value={(f.moodboard || [])[k] || ""} onChange={(v) => { const m = [...(f.moodboard || ["", "", ""])]; while (m.length < 3) m.push(""); m[k] = v; setF({ ...f, moodboard: m.filter(Boolean).length ? m : [] }); }} />
        ))}
      </div>
      <div className="mt-4 flex justify-end">
        <button data-testid={`event-save-${e.slug}`} onClick={() => onSave(e.slug, f)} className="bg-[#522C36] text-[#F8F5F0] overline px-6 py-3">Save</button>
      </div>
    </div>
  );
}

function ContentTab() {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => setErr(true)); }, []);
  if (err) return <LoadError />;
  if (!s) return <div>Loading…</div>;
  const save = async () => { await api.put("/admin/settings", { data: s }); toast.success("Saved"); };
  return (
    <div data-testid="content-tab">
      <h1 className="font-serif-display text-3xl mb-8">Site content</h1>
      <div className="space-y-5 max-w-2xl">
        <Field label="Tagline"><input value={s.tagline || ""} onChange={(e) => setS({ ...s, tagline: e.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
        <Field label="Wedding date (ISO)"><input value={s.wedding_date || ""} onChange={(e) => setS({ ...s, wedding_date: e.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
        <Field label="RSVP deadline"><input value={s.rsvp_deadline || ""} onChange={(e) => setS({ ...s, rsvp_deadline: e.target.value })} className="w-full border-b border-[#B69A69]/40 py-2 bg-transparent" /></Field>
        <button data-testid="save-settings" onClick={save} className="bg-[#522C36] text-[#F8F5F0] overline px-6 py-3">Save settings</button>
      </div>
    </div>
  );
}

function AnalyticsTab() {
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)).catch(() => setErr(true)); }, []);
  if (err) return <LoadError />;
  if (!a) return <div>Loading…</div>;
  return (
    <div data-testid="analytics-tab">
      <h1 className="font-serif-display text-3xl mb-8">RSVP by event</h1>
      <div className="grid md:grid-cols-2 gap-6">
        {a.events.map((e) => (
          <div key={e.name} className="border border-[#E8D9C5] p-6">
            <div className="font-serif-display text-2xl mb-4">{e.name}</div>
            <div className="grid grid-cols-4 gap-4 text-center">
              <Micro label="Invited" value={e.invited} />
              <Micro label="Attending" value={e.attending} tone="good" />
              <Micro label="Declined" value={e.declined} />
              <Micro label="Pending" value={e.pending} tone="warn" />
            </div>
            <div className="mt-4 flex gap-4 text-sm overline text-[#292524]/70">
              <span>Children: {e.children}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Micro({ label, value, tone }) {
  const color = tone === "good" ? "#522C36" : tone === "warn" ? "#B69A69" : "#292524";
  return (
    <div>
      <div className="font-serif-display text-3xl" style={{ color }}>{value}</div>
      <div className="overline text-[#B69A69] mt-1">{label}</div>
    </div>
  );
}
